import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BulkActionsState, BulkActionRow } from './types';

const initialState: BulkActionsState = {
  phase: 'landing',
  rows: [],
  selectedStatus: null,
  processing: {
    processed: 0,
    failed: 0,
    total: 0,
  },
  failedRows: [],
};

const bulkActionsSlice = createSlice({
  name: 'bulkActions',
  initialState,
  reducers: {
    setRows: (state, action: PayloadAction<BulkActionRow[]>) => {
      state.rows = action.payload;
    },
    removeRow: (state, action: PayloadAction<number>) => {
      state.rows = state.rows.filter(r => r.threatId !== action.payload);
    },
    setPhase: (state, action: PayloadAction<BulkActionsState['phase']>) => {
      state.phase = action.payload;
    },
    setSelectedStatus: (state, action: PayloadAction<string | null>) => {
      state.selectedStatus = action.payload;
    },
    startProcessing: state => {
      state.phase = 'processing';
      state.processing = {
        processed: 0,
        failed: 0,
        total: state.rows.length,
      };
      state.failedRows = [];
    },
    markRowSuccess: (state, action: PayloadAction<string>) => {
      const row = state.rows.find(r => r.threatUrl === action.payload);
      if (row) {
        row.status = 'success';
      }
      state.processing.processed += 1;
    },
    markRowFailed: (
      state,
      action: PayloadAction<{ threatName: string; error: string }>,
    ) => {
      const row = state.rows.find(
        r => r.threatUrl === action.payload.threatName,
      );
      if (row) {
        row.status = 'failed';
        row.errorReason = action.payload.error;
        state.failedRows.push({ ...row });
      }
      state.processing.processed += 1;
      state.processing.failed += 1;
    },
    addSkippedBatchFailures: (
      state,
      action: PayloadAction<BulkActionRow[]>,
    ) => {
      action.payload.forEach(row => {
        const existing = state.rows.find(r => r.threatId === row.threatId);
        if (existing) {
          existing.status = 'failed';
          existing.errorReason =
            row.errorReason || 'Batch request failed after 3 retries';
        }
        state.failedRows.push({
          ...row,
          status: 'failed',
          errorReason:
            row.errorReason || 'Batch request failed after 3 retries',
        });
      });
      state.processing.processed += action.payload.length;
      state.processing.failed += action.payload.length;
    },
    completeProcessing: state => {
      state.phase = 'completed';
    },
    resetBulkActions: () => initialState,
  },
});

export const {
  setRows,
  removeRow,
  setPhase,
  setSelectedStatus,
  startProcessing,
  markRowSuccess,
  markRowFailed,
  addSkippedBatchFailures,
  completeProcessing,
  resetBulkActions,
} = bulkActionsSlice.actions;

export default bulkActionsSlice.reducer;
